import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  TrendingUp,
  TrendingDown,
  Smartphone,
  Users,
  DollarSign,
  Download,
  Eye,
  Star,
  Calendar
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalApps: 0,
    totalRevenue: 0,
    totalDownloads: 0,
    activeUsers: 0,
    monthlyGrowth: 0,
    revenueGrowth: 0
  });
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [mobileAppsStats, setMobileAppsStats] = useState({
    totalUsers: 0,
    totalDownloads: 0,
    totalApps: 0
  });

  // Fonksiyonları doğru sırada tanımlayalım
  const calculateStatsFromApps = useCallback(() => {
    console.log('📊 Dashboard - Stats hesaplanıyor...');
    console.log('📊 Panel apps:', apps);
    console.log('📊 Mobile apps stats:', mobileAppsStats);

    // Panel uygulamaları
    const panelApps = apps.length;
    const panelDownloads = apps.reduce((sum, app) => sum + (app.metrics?.totalDownloads || 0), 0);
    const panelRevenue = apps.reduce((sum, app) => sum + (app.revenue?.totalRevenue || 0), 0);
    const panelActiveUsers = apps.reduce((sum, app) => sum + (app.metrics?.activeUsers || 0), 0);

    // Mobil uygulamalar + Panel uygulamaları
    const totalApps = panelApps + (mobileAppsStats.totalApps || 0);
    const totalDownloads = panelDownloads + (mobileAppsStats.totalDownloads || 0);
    const totalActiveUsers = panelActiveUsers + (mobileAppsStats.totalUsers || 0);

    const calculatedStats = {
      totalApps,
      totalDownloads,
      totalRevenue: panelRevenue, // Sadece panel uygulamalarından gelir
      activeUsers: totalActiveUsers,
      monthlyGrowth: totalDownloads > 0 ? 5.2 : 0,
      revenueGrowth: panelRevenue > 0 ? 3.1 : 0
    };

    setStats(calculatedStats);

    console.log('📊 Dashboard - Final combined stats:', { 
      panelApps, 
      panelDownloads,
      panelRevenue,
      panelActiveUsers,
      mobileApps: mobileAppsStats.totalApps,
      mobileDownloads: mobileAppsStats.totalDownloads,
      mobileUsers: mobileAppsStats.totalUsers,
      totalApps, 
      totalDownloads, 
      totalActiveUsers,
      calculatedStats
    });
  }, [apps, mobileAppsStats]);

  const calculatePlatformDataFromApps = useCallback(() => {
    if (apps.length > 0) {
      const androidCount = apps.filter(app => app.platform === 'android' || app.platform === 'both').length;
      const iosCount = apps.filter(app => app.platform === 'ios' || app.platform === 'both').length;
      const total = Math.max(androidCount + iosCount, 1);

      setPlatformData([
        { name: 'Android', value: Math.round((androidCount / total) * 100), color: '#10B981' },
        { name: 'iOS', value: Math.round((iosCount / total) * 100), color: '#3B82F6' }
      ]);
    } else {
      setPlatformData([
        { name: 'Android', value: 65, color: '#10B981' },
        { name: 'iOS', value: 35, color: '#3B82F6' }
      ]);
    }
  }, [apps]);

  const fetchApps = useCallback(async () => {
    try {
      const response = await axios.get('/apps');
      if (response.data.success) {
        setApps(response.data.apps || []);
      }
    } catch (error) {
      console.error('Uygulamalar hatası:', error);
      setApps([]);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/analytics/dashboard-stats');
      if (response.data.success) {
        setStats(response.data.stats);
      } else {
        // API başarısız ise apps verilerinden hesapla
        calculateStatsFromApps();
      }
    } catch (error) {
      console.error('İstatistikler hatası:', error);
      // API hatası durumunda apps verilerinden hesapla
      calculateStatsFromApps();
    }
  }, [calculateStatsFromApps]);

  const fetchPlatformData = useCallback(async () => {
    try {
      const response = await axios.get('/analytics/platform-distribution');
      if (response.data.success) {
        setPlatformData(response.data.data || []);
      } else {
        // Apps verilerinden platform dağılımını hesapla
        calculatePlatformDataFromApps();
      }
    } catch (error) {
      console.error('Platform dağılımı hatası:', error);
      // Apps verilerinden platform dağılımını hesapla
      calculatePlatformDataFromApps();
    }
  }, [calculatePlatformDataFromApps]);

  const fetchRevenueData = useCallback(async () => {
    try {
      const response = await axios.get('/analytics/revenue-trend');
      if (response.data.success) {
        setRevenueData(response.data.data || []);
      } else {
        // Fallback veri - Gelir API'si yok
        setRevenueData([
          { name: 'Haz', revenue: 0 },
          { name: 'Tem', revenue: 0 },
          { name: 'Ağu', revenue: 0 },
          { name: 'Eyl', revenue: 0 },
          { name: 'Eki', revenue: 0 },
          { name: 'Kas', revenue: 0 }
        ]);
      }
    } catch (error) {
      console.error('Gelir trendi hatası:', error);
      // Fallback veri - Gelir API'si yok
      setRevenueData([
        { name: 'Haz', revenue: 0 },
        { name: 'Tem', revenue: 0 },
        { name: 'Ağu', revenue: 0 },
        { name: 'Eyl', revenue: 0 },
        { name: 'Eki', revenue: 0 },
        { name: 'Kas', revenue: 0 }
      ]);
    }
  }, []);

  const fetchMobileAppsStats = useCallback(async () => {
    try {
      console.log('📱 Dashboard - Mobil uygulama istatistikleri çekiliyor...');
      const response = await axios.get('/mobile-apps/stats');
      console.log('📱 Dashboard - API yanıtı:', response.data);
      
      if (response.data.success) {
        const stats = response.data.stats;
        setMobileAppsStats({
          totalUsers: stats.totalUsers || 0,
          totalDownloads: stats.totalDownloads || 0,
          totalApps: stats.totalApps || 0,
          averageRating: stats.averageRating || 0
        });
        console.log('✅ Dashboard - Mobile apps stats güncellendi:', stats);
      } else {
        console.log('⚠️ Dashboard - Mobile apps API başarısız:', response.data);
        setMobileAppsStats({
          totalUsers: 0,
          totalDownloads: 0,
          totalApps: 0,
          averageRating: 0
        });
      }
    } catch (error) {
      console.error('❌ Dashboard - Mobil uygulama istatistikleri hatası:', error);
      // Fallback veri
      setMobileAppsStats({
        totalUsers: 0,
        totalDownloads: 0,
        totalApps: 0,
        averageRating: 0
      });
    }
  }, []);

  // Sonsuz döngüyü önlemek için ref kullanıyoruz
  const isInitialMount = useRef(true);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // API çağrılarını sıralı yapalım (rate limiting için)
      await fetchApps();

      // 500ms bekle
      await new Promise(resolve => setTimeout(resolve, 500));

      // Diğer API çağrıları opsiyonel - hata olursa fallback kullan
      try {
        await fetchStats();
      } catch (error) {
        console.log('Stats API hatası, fallback kullanılıyor:', error.message);
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        await fetchPlatformData();
      } catch (error) {
        console.log('Platform API hatası, fallback kullanılıyor:', error.message);
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        await fetchRevenueData();
      } catch (error) {
        console.log('Revenue API hatası, fallback kullanılıyor:', error.message);
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        await fetchMobileAppsStats();
      } catch (error) {
        console.log('Mobile apps stats API hatası, fallback kullanılıyor:', error.message);
      }

    } catch (error) {
      console.error('Dashboard veri hatası:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchApps, fetchStats, fetchPlatformData, fetchRevenueData, fetchMobileAppsStats]);

  useEffect(() => {
    if (isInitialMount.current) {
      fetchDashboardData();
      isInitialMount.current = false;
    }
  }, [fetchDashboardData]);

  // Apps verisi veya mobil uygulama stats'ı değiştiğinde yeniden hesapla
  useEffect(() => {
    if (apps.length > 0 || mobileAppsStats.totalApps > 0) {
      calculateStatsFromApps();
    }
  }, [apps, mobileAppsStats, calculateStatsFromApps]);

  // En iyi uygulamaları hesapla
  const topApps = apps
    .sort((a, b) => (b.revenue?.totalRevenue || 0) - (a.revenue?.totalRevenue || 0))
    .slice(0, 4)
    .map(app => ({
      name: app.name,
      downloads: app.metrics?.totalDownloads || 0,
      revenue: app.revenue?.totalRevenue || 0,
      rating: app.metrics?.rating || 0
    }));



  const StatCard = ({ title, value, change, icon: Icon, color = 'blue' }) => {
    const isPositive = change > 0;
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500'
    };

    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`p-3 rounded-md ${colorClasses[color]}`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-700 truncate">{title}</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-black">
                    {typeof value === 'number' ? value.toLocaleString('tr-TR') : value}
                  </div>
                  {change !== undefined && (
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {isPositive ? (
                        <TrendingUp className="self-center flex-shrink-0 h-4 w-4 mr-1" />
                      ) : (
                        <TrendingDown className="self-center flex-shrink-0 h-4 w-4 mr-1" />
                      )}
                      <span>{Math.abs(change)}%</span>
                    </div>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="lg:flex lg:items-center lg:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-7 text-gray-900 truncate">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Cezec Dev ekibinin genel durumu
          </p>
        </div>
        <div className="mt-4 flex lg:mt-0 lg:ml-4">
          <span className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <Calendar className="h-4 w-4 mr-2" />
            Son 30 Gün
          </span>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-5">
        <StatCard
          title="Toplam Uygulama"
          value={stats.totalApps}
          change={stats.monthlyGrowth}
          icon={Smartphone}
          color="blue"
        />
        <StatCard
          title="Toplam Gelir"
          value={`₺${stats.totalRevenue.toLocaleString('tr-TR')}`}
          change={stats.revenueGrowth}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Toplam İndirme"
          value={stats.totalDownloads}
          change={15.2}
          icon={Download}
          color="purple"
        />
        <StatCard
          title="Aktif Kullanıcı"
          value={stats.activeUsers}
          change={7.8}
          icon={Users}
          color="orange"
        />
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Gelir Trendi */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-4 sm:p-6">
            <h3 className="text-base sm:text-lg leading-6 font-medium text-gray-900 mb-4">
              Aylık Gelir Trendi
            </h3>
            <div className="h-64 sm:h-80">
              {revenueData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => [
                        name === 'revenue' ? `₺${value.toLocaleString('tr-TR')}` : value.toLocaleString('tr-TR'),
                        name === 'revenue' ? 'Gelir' : 'İndirme'
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Henüz gelir verisi bulunmuyor</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Platform Dağılımı */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-4 sm:p-6">
            <h3 className="text-base sm:text-lg leading-6 font-medium text-gray-900 mb-4">
              Platform Dağılımı
            </h3>
            <div className="h-64 sm:h-80">
              {platformData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={platformData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {platformData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`%${value}`, 'Oran']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center space-x-6 mt-4">
                    {platformData.map((item, index) => (
                      <div key={index} className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm text-gray-600">{item.name} (%{item.value})</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Smartphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Henüz platform verisi bulunmuyor</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobil Uygulamalar Özeti */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            📱 Mobil Uygulamalar Özeti
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Tüm mobil uygulamalardan toplanan veriler
          </p>
        </div>
        <div className="px-4 py-4 sm:px-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Smartphone className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {mobileAppsStats.totalApps.toLocaleString('tr-TR')}
                  </div>
                  <div className="text-sm text-blue-700">Mobil Uygulama</div>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {mobileAppsStats.totalUsers.toLocaleString('tr-TR')}
                  </div>
                  <div className="text-sm text-green-700">Aktif Kullanıcı</div>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Download className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-purple-600">
                    {mobileAppsStats.totalDownloads.toLocaleString('tr-TR')}
                  </div>
                  <div className="text-sm text-purple-700">Toplam İndirme</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* En İyi Performans Gösteren Uygulamalar */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            En İyi Performans Gösteren Panel Uygulamaları
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Panel'de kayıtlı uygulamaların performansı
          </p>
        </div>
        <ul className="divide-y divide-gray-200">
          {topApps.length > 0 ? topApps.map((app, index) => (
            <li key={index} className="px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                      <Smartphone className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{app.name}</div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Download className="h-4 w-4 mr-1" />
                      {app.downloads.toLocaleString('tr-TR')} indirme
                      <Star className="h-4 w-4 ml-3 mr-1 text-yellow-400" />
                      {app.rating}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    ₺{app.revenue.toLocaleString('tr-TR')}
                  </div>
                  <div className="text-sm text-gray-500">Bu ay</div>
                </div>
              </div>
            </li>
          )) : (
            <li className="px-4 py-8 text-center">
              <Smartphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Henüz panel uygulaması eklenmemiş</p>
            </li>
          )}
        </ul>
      </div>

      {/* Hızlı Eylemler */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Hızlı Eylemler
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
            <button className="relative group bg-gray-50 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 rounded-lg hover:bg-gray-100">
              <div>
                <span className="rounded-lg inline-flex p-3 bg-blue-500 text-white">
                  <Smartphone className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Yeni Uygulama Ekle
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Sisteme yeni bir mobil uygulama ekleyin
                </p>
              </div>
            </button>

            <button className="relative group bg-gray-50 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-green-500 rounded-lg hover:bg-gray-100">
              <div>
                <span className="rounded-lg inline-flex p-3 bg-green-500 text-white">
                  <DollarSign className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Gelir Güncelle
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Uygulama gelirlerini güncelleyin
                </p>
              </div>
            </button>

            <button className="relative group bg-gray-50 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-purple-500 rounded-lg hover:bg-gray-100">
              <div>
                <span className="rounded-lg inline-flex p-3 bg-purple-500 text-white">
                  <Users className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Ekip Üyesi Ekle
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Yeni ekip üyesi oluşturun
                </p>
              </div>
            </button>

            <button className="relative group bg-gray-50 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-500 rounded-lg hover:bg-gray-100">
              <div>
                <span className="rounded-lg inline-flex p-3 bg-orange-500 text-white">
                  <Eye className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Rapor Oluştur
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Detaylı analiz raporu çıkarın
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;